import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Grid,
  Box,
  IconButton,
  Chip,
  Alert,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";

import { useNavigate } from "react-router-dom";

import { getAllLiquidations, deleteLiquidation } from "../../services/LiquidationService";
import {
  getAllIndividualLiquidations,
  deleteIndividualLiquidation,
} from "../../services/IndividualLiquidationService";
import { findLegalFileByCode, getLegalFilesById } from "../../services/LegalFileService";
import { downloadLegalFileDocument } from "../../services/LegalFileAttachmentService";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import DatagridCustom from "../General/DatagridCustom";
import ActionHeaderCard from "../General/ActionHeaderCard";
import PreviewDialog from "../General/Dialog/PreviewDialog";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import LiquidationFormDialog from "./LiquidationFormDialog";
import IndividualLiquidationFormDialog from "../IndividualLiquidation/IndividualLiquidationFormDialog";
import { formatCurrency } from "../../config/Helper";

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("es-AR");
}

const KIND_LABEL = { municipality: "Municipalidad", common: "Común" };
const KIND_STYLE = {
  municipality: { bg: "rgba(157,23,77,0.12)", fg: "#9d174d" },
  common: { bg: "rgba(37,99,235,0.12)", fg: "#2563eb" },
};

function VersionHistoryCell({ history, onPreview }) {
  const [anchor, setAnchor] = useState(null);
  if (!history?.length) return "—";
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{
          borderRadius: 2,
          width: 34,
          height: 34,
          border: "1px solid rgba(124,58,237,0.22)",
          bgcolor: "rgba(124,58,237,0.07)",
          "&:hover": { bgcolor: "rgba(124,58,237,0.14)" },
        }}
      >
        <Badge
          badgeContent={history.length}
          sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 15, height: 15, bgcolor: "#7c3aed", color: "#fff" } }}
        >
          <HistoryRoundedIcon sx={{ fontSize: 18, color: "#7c3aed" }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
              minWidth: 200,
              overflow: "hidden",
            },
          },
        }}
      >
        {history.map((liq) => (
          <MenuItem key={liq.id} onClick={() => { setAnchor(null); onPreview(liq); }} dense sx={{ gap: 1.5, py: 1 }}>
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <VisibilityRoundedIcon sx={{ fontSize: 16, opacity: 0.55 }} />
            </ListItemIcon>
            <ListItemText
              primary={`Versión ${liq.version}`}
              secondary={fmtDate(liq.created_at)}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function ManagerLiquidations() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canSeeMuni = hasPermission("ver liquidaciones");
  const canSeeCommon = hasPermission("ver liquidaciones comunes");
  const canDeleteMuni = hasPermission("eliminar liquidacion");
  const canDeleteCommon = hasPermission("eliminar liquidacion comun");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const blobUrlRef = useRef(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCode, setSearchCode] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [foundFile, setFoundFile] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formKind, setFormKind] = useState("municipality");
  const [fullLegalFile, setFullLegalFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [muniRes, commonRes] = await Promise.allSettled([
        canSeeMuni ? getAllLiquidations() : Promise.resolve(null),
        canSeeCommon ? getAllIndividualLiquidations() : Promise.resolve(null),
      ]);

      const pick = (res) =>
        res.status === "fulfilled" && res.value ? res.value.data?.data ?? res.value.data ?? [] : [];

      const muni = pick(muniRes).map((r) => ({ ...r, _kind: "municipality" }));
      const common = pick(commonRes).map((r) => ({ ...r, _kind: "common" }));

      if (canSeeMuni && muniRes.status === "rejected" && canSeeCommon && commonRes.status === "rejected") {
        setError("No se pudieron cargar las liquidaciones.");
        return;
      }
      if (muniRes.status === "rejected" && canSeeMuni) showToast("No se pudieron cargar las de municipalidad.", "error");
      if (commonRes.status === "rejected" && canSeeCommon) showToast("No se pudieron cargar las comunes.", "error");

      setRows([...muni, ...common].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
    } catch {
      setError("No se pudieron cargar las liquidaciones.");
    } finally {
      setLoading(false);
    }
  }, [canSeeMuni, canSeeCommon, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(
    () => (kindFilter === "all" ? rows : rows.filter((r) => r._kind === kindFilter)),
    [rows, kindFilter],
  );

  const openDoc = async (documentId, label) => {
    if (!documentId) {
      showToast("Esta liquidación todavía no tiene un PDF generado.", "info");
      return;
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewUrl(null);
    setPreviewTitle(label);
    try {
      const response = await downloadLegalFileDocument(documentId);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      showToast("No se pudo cargar el PDF generado.", "error");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const handleOpenSearch = () => {
    setSearchCode("");
    setSearchError("");
    setFoundFile(null);
    setFullLegalFile(null);
    setSearchOpen(true);
  };

  const handleSearch = async () => {
    if (!searchCode.trim()) return;
    setSearchLoading(true);
    setSearchError("");
    setFoundFile(null);
    setFullLegalFile(null);
    try {
      const res = await findLegalFileByCode(searchCode.trim());
      const basic = res.data?.data ?? res.data;
      if (!basic?.id) throw new Error("not_found");
      const fullRes = await getLegalFilesById(basic.id);
      const full = fullRes.data?.data ?? fullRes.data;
      const isExternal = full?.type === "external";
      if (isExternal && !canSeeCommon) {
        setSearchError("Es un expediente común y no tenés permiso para liquidaciones comunes.");
        return;
      }
      if (!isExternal && !canSeeMuni) {
        setSearchError("No tenés permiso para crear liquidaciones de municipalidad.");
        return;
      }
      setFoundFile(full);
      setFullLegalFile(full);
      setFormKind(isExternal ? "common" : "municipality");
    } catch {
      setSearchError("No se encontró el expediente con ese código.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    setFullLegalFile(null);
    setFoundFile(null);
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const fn = deleteTarget._kind === "common" ? deleteIndividualLiquidation : deleteLiquidation;
      await fn(deleteTarget.id);
      showToast("Liquidación eliminada correctamente.", "success");
      setDeleteTarget(null);
      load();
    } catch {
      showToast("No se pudo eliminar la liquidación.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      field: "_kind",
      headerName: "Tipo",
      flex: 0.9,
      renderCell: (params) => {
        const st = KIND_STYLE[params.value] ?? KIND_STYLE.municipality;
        return (
          <Chip
            size="small"
            label={KIND_LABEL[params.value] ?? params.value}
            sx={{ borderRadius: 999, fontWeight: 700, bgcolor: st.bg, color: st.fg }}
          />
        );
      },
    },
    {
      field: "legal_file",
      headerName: "Expediente",
      flex: 3.5,
      valueGetter: (params) => params?.name || params?.code || "—",
      renderCell: (params) => {
        const lf = params.row.legal_file;
        if (!lf) return "—";
        return (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
            onClick={() => navigate(`/legal/file/${lf.id}`)}
          >
            {lf.name || lf.code || lf.id}
            <OpenInNewRoundedIcon sx={{ fontSize: 13, opacity: 0.5, flexShrink: 0 }} />
          </Box>
        );
      },
    },
    {
      field: "version",
      headerName: "Versión",
      flex: 0.6,
      renderCell: (params) => (
        <Chip size="small" label={`v${params.value}`} sx={{ borderRadius: 999, bgcolor: "rgba(124,58,237,0.10)", color: "#7c3aed" }} />
      ),
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => formatCurrency(params ?? 0),
    },
    {
      field: "created_at",
      headerName: "Fecha",
      flex: 0.8,
      valueGetter: (params) => fmtDate(params),
    },
    {
      field: "history",
      headerName: "Versiones",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <VersionHistoryCell
          history={params.value ?? []}
          onPreview={(liq) =>
            openDoc(
              liq.legal_file_document_id,
              `${params.row.legal_file?.name ?? params.row.legal_file?.code ?? "—"} · v${liq.version} (PDF)`,
            )
          }
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const canDeleteRow = params.row._kind === "common" ? canDeleteCommon : canDeleteMuni;
        return (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
            <IconButton
              size="small"
              color="primary"
              title="Ver PDF generado"
              onClick={() =>
                openDoc(
                  params.row.legal_file_document_id,
                  `${params.row.legal_file?.name ?? params.row.legal_file?.code ?? params.row.id} · v${params.row.version} (PDF)`,
                )
              }
            >
              <PictureAsPdfRoundedIcon fontSize="small" />
            </IconButton>
            {canDeleteRow && (
              <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<CalculateRoundedIcon />}
            title="Liquidaciones"
            description="Todas las liquidaciones del sistema: municipalidad y comunes."
            action={
              (canSeeMuni || canSeeCommon) && (
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={handleOpenSearch}
                  sx={{ borderRadius: 2, fontWeight: 800 }}
                >
                  Nueva Liquidación
                </Button>
              )
            }
          />
        </Grid>

        {canSeeMuni && canSeeCommon && (
          <Grid size={{ xs: 12 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={kindFilter}
              onChange={(_, v) => v && setKindFilter(v)}
              sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 800, px: 2 } }}
            >
              <ToggleButton value="all">Todas ({rows.length})</ToggleButton>
              <ToggleButton value="municipality">Municipalidad ({rows.filter((r) => r._kind === "municipality").length})</ToggleButton>
              <ToggleButton value="common">Comunes ({rows.filter((r) => r._kind === "common").length})</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            {error ? (
              <Alert severity="error" action={<Button size="small" onClick={load}>Reintentar</Button>}>
                {error}
              </Alert>
            ) : (
              <DatagridCustom
                rows={visibleRows}
                columns={columns}
                loading={loading}
                getRowId={(row) => `${row._kind}-${row.id}`}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <PreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        title={previewTitle}
        subtitle="PDF generado de la liquidación"
        url={previewUrl}
        loading={previewLoading}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Eliminar liquidación"
        description={`¿Eliminar la liquidación ${
          deleteTarget?._kind === "common" ? "común" : "de municipalidad"
        } v${deleteTarget?.version ?? ""} del expediente ${
          deleteTarget?.legal_file?.name ?? deleteTarget?.legal_file?.code ?? ""
        }? Se borrarán los ítems y el PDF generado. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="error"
      />

      <AppDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Buscar Expediente"
        subtitle="Ingresá el código del expediente. El tipo de liquidación se elige según el expediente."
        maxWidth="sm"
        icon={<SearchRoundedIcon />}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Código de expediente"
              value={searchCode}
              onChange={(e) => { setSearchCode(e.target.value); setSearchError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              size="small"
              fullWidth
              autoFocus
              error={Boolean(searchError)}
              helperText={searchError || " "}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!searchCode.trim() || searchLoading}
              startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchRoundedIcon />}
              sx={{ borderRadius: 2, fontWeight: 800, flexShrink: 0, height: 40, alignSelf: "flex-start", mt: "1px" }}
            >
              Buscar
            </Button>
          </Box>

          {foundFile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "success.main",
                bgcolor: "rgba(46,125,50,0.06)",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.3 }}>
                  {foundFile.name || foundFile.code}
                </Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.6, mt: 0.3 }}>{foundFile.code}</Typography>
                <Chip
                  size="small"
                  label={`Liquidación ${formKind === "common" ? "Común" : "de Municipalidad"}`}
                  sx={{
                    mt: 0.8,
                    borderRadius: 999,
                    fontWeight: 700,
                    bgcolor: KIND_STYLE[formKind].bg,
                    color: KIND_STYLE[formKind].fg,
                  }}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => { setSearchOpen(false); setFormOpen(true); }}
                sx={{ borderRadius: 2, fontWeight: 800, flexShrink: 0 }}
              >
                Crear
              </Button>
            </Box>
          )}
        </Box>
      </AppDialog>

      <LiquidationFormDialog
        open={formOpen && formKind === "municipality"}
        onClose={() => { setFormOpen(false); setFullLegalFile(null); }}
        legalFile={fullLegalFile}
        onSaved={handleFormSaved}
      />

      <IndividualLiquidationFormDialog
        open={formOpen && formKind === "common"}
        onClose={() => { setFormOpen(false); setFullLegalFile(null); }}
        legalFile={fullLegalFile}
        onSaved={handleFormSaved}
      />
    </>
  );
}
